import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import drugsApiService from '../../services/drugsapi.service';
import { useNavigate } from 'react-router';
import debounce from 'lodash/debounce';
import { IconSearch } from '@tabler/icons-react';

import loadingGif from '../../assets/images/loader.gif';

const DrugSearchSchema = Yup.object().shape({
  drugName: Yup.string().required('Please enter a drug name'),
});

function DrugSearchForm({ drugName }) {
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState([]);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const debouncedSearch = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 3) {
        setSearchResults([]);
        return;
      }
      try {
        setIsLoading(true);
        setShowDropdown(true);

        const drugSearchResults =
          await drugsApiService.getDrugSearch(searchTerm);

        setSearchResults(drugSearchResults.results);
      } catch (error) {
        console.error('Error searching drugs:', error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    [],
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the dropdown and input field
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = () => {
    if (selectedDrug) {
      navigate(`/landing/drug-search/${selectedDrug.DispensableDrugID}`);
    }
  };

  const handleDrugSelect = (drug, setFieldValue) => {
    setSelectedDrug(drug);
    setFieldValue('drugName', drug.DispensableDrugDesc);
    setShowDropdown(false);
  };

  return (
    <>
      <Formik
        initialValues={{
          drugName: drugName,
        }}
        validationSchema={DrugSearchSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, setFieldValue, values }) => (
          <Form className="drug-search-form">
            <div className="col-lg-12 col-xl-12 col-12 mb-4 d-flex">
              <div className="flex-grow-1 me-md-3 position-relative">
                <Field
                  type="text"
                  name="drugName"
                  autoComplete="off"
                  innerRef={inputRef}
                  className={`form-control search-input ${
                    errors.drugName && touched.drugName ? 'is-invalid' : ''
                  }`}
                  placeholder="Atorvastatin..."
                  onChange={(e) => {
                    setFieldValue('drugName', e.target.value);
                    setSelectedDrug(null);
                    debouncedSearch(e.target.value);
                  }}
                  onFocus={() => {
                    if (values.drugName) setShowDropdown(true);
                  }}
                  aria-expanded={showDropdown}
                />
                {errors.drugName && touched.drugName && (
                  <>
                    <span className="invalid-feedback drug-input-error">
                      {errors.drugName}
                    </span>
                  </>
                )}

                {/* Move the dropdown inside this container */}
                {showDropdown && (values.drugName || isLoading) && (
                  <div
                    ref={dropdownRef}
                    className="dropdown-menu show"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      width: '100%',
                      zIndex: 1050,
                    }}
                  >
                    {isLoading ? (
                      <div className="dropdown-item m-0 p-0">
                        <span className="text-primary">
                          <img
                            style={{
                              maxHeight: '80px',
                              height: '60px',
                              display: 'block',
                              margin: '0 auto',
                            }}
                            src={loadingGif}
                            alt="Loading..."
                            className="loader-gif"
                          />
                        </span>
                      </div>
                    ) : searchResults.length > 0 ? (
                      <div
                        style={{
                          maxHeight: '200px',
                          width: '100%',
                          overflowY: 'auto',
                        }}
                      >
                        {searchResults.map((drug) => (
                          <button
                            style={{
                              whiteSpace: 'normal',
                            }}
                            key={drug.DispensableDrugID}
                            type="button"
                            className="dropdown-item"
                            onClick={() =>
                              handleDrugSelect(drug, setFieldValue)
                            }
                          >
                            {drug.DispensableDrugDesc}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="dropdown-item text-muted text-center">
                        No results found
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary width-auto py-2 px-3 max-h-42"
                disabled={!selectedDrug}
              >
                <IconSearch size={18} />
                {'Search'}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}

DrugSearchForm.propTypes = {
  drugName: PropTypes.string,
};
export default DrugSearchForm;
